"use client";

import { type MotionValue, frame, cancelFrame } from "motion/react";
import { useEffect, useRef } from "react";
import {
  type Anchor,
  chladni,
  type ChladniParamsDefault,
  type ChladniParamsSources,
  type Contours,
  type Point,
  sourcesFor,
  type Symmetry,
} from "@ui/lib/chladni";

const DEBUG = false;

type MotionRecord<T> = {
  [Key in keyof T]: T[Key] extends [number, number]
    ? [MotionValue<number> | number, MotionValue<number> | number]
    : MotionValue<T[Key]> | T[Key];
};

type MotionChladniParams =
  | ({ mode: "sources"; symmetry?: Symmetry | "auto" } & MotionRecord<Omit<ChladniParamsSources, "mode" | "symmetry">>)
  | ({ mode: "default"; anchor?: Anchor; symmetry?: Symmetry | "auto" } & MotionRecord<
      Required<Omit<ChladniParamsDefault, "mode" | "anchor" | "symmetry">>
    >);

function createPath2DFromPoints(points: Array<Point>) {
  if (points.length < 2) return null;

  const path = new Path2D();
  path.moveTo(points[0].x, points[0].y);

  for (let i = 1; i < points.length; i++) {
    path.lineTo(points[i].x, points[i].y);
  }

  return path;
}

function getValue(value: number | MotionValue<number>) {
  return typeof value === "number" ? value : value.get();
}

function getVelocity(value: number | MotionValue<number>) {
  return typeof value === "number" ? 0 : value.getVelocity();
}

export function ChladniCanvas({
  strokeWidth = 1,
  strokeOpacity = 1,
  stroke = "black",
  onDraw,
  renderMode = "onChange",
  ...params
}: MotionChladniParams & {
  strokeWidth?: number;
  strokeOpacity?: number;
  stroke?: string;
  onDraw?: () => void;
  renderMode?: "onChange" | "always";
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio;
    canvas.width = canvas.offsetWidth * dpr;
    canvas.height = canvas.offsetHeight * dpr;

    function drawContours({ paths, symmetry }: Contours) {
      if (!canvas) return;
      if (!ctx) return;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Drawing style
      ctx.strokeStyle = stroke;
      ctx.lineWidth = strokeWidth * dpr;
      ctx.globalAlpha = strokeOpacity;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";

      if (symmetry !== "none") {
        const path2DObjects: Array<Path2D> = [];

        paths.forEach(path => {
          const path2D = createPath2DFromPoints(path);
          if (path2D) {
            path2DObjects.push(path2D);
          }
        });

        if (symmetry === "quadrant") {
          // Draw all 4 quadrants using mirror transforms
          const mirrors = [
            [1, 1], // Original (bottom-right quadrant)
            [-1, 1], // Horizontally mirrored (bottom-left quadrant)
            [1, -1], // Vertically mirrored (top-right quadrant)
            [-1, -1], // Both mirrored (top-left quadrant)
          ];

          mirrors.forEach(([scaleX, scaleY]) => {
            ctx.save();
            ctx.translate(canvas.width / 2, canvas.height / 2);
            ctx.scale(scaleX, scaleY);
            ctx.translate(-(canvas.width / 2), canvas.height / -2);

            path2DObjects.forEach(path2D => {
              ctx.stroke(path2D);
            });

            ctx.restore();
          });
        } else if (symmetry === "bilateral") {
          // Draw original and mirrored version
          path2DObjects.forEach(path2D => {
            ctx.stroke(path2D);
          });

          // Draw mirrored version across logical center
          ctx.save();
          ctx.translate(canvas.width / 2, canvas.height / 2);
          ctx.scale(-1, 1); // Mirror across Y-axis
          ctx.translate(-(canvas.width / 2), canvas.height / -2);

          path2DObjects.forEach(path2D => {
            ctx.stroke(path2D);
          });

          ctx.restore();
        }
      } else {
        // Standard rendering for non-symmetric cases
        paths.forEach(path => {
          const path2D = createPath2DFromPoints(path);
          if (path2D) {
            ctx.stroke(path2D);
          }
        });
      }

      if (DEBUG && params.mode === "sources") {
        // Draw source nodes as small white dots
        const n = getValue(params.sources);

        ctx.fillStyle = "white";
        ctx.strokeStyle = "#333";
        ctx.lineWidth = 1;
        ctx.globalAlpha = 1;

        sourcesFor(n, canvas.width, canvas.height).forEach(({ x, y }) => {
          ctx.beginPath();
          ctx.arc(x, y, 4, 0, 2 * Math.PI);
          ctx.fill();
          ctx.stroke();
        });
      }
    }

    function draw(force = false) {
      if (!canvas) return;

      // Skip rendering if the values are not changing
      if (
        force === false &&
        ((params.mode === "sources" &&
          getVelocity(params.frequency) === 0 &&
          getVelocity(params.phaseShift) === 0 &&
          getVelocity(params.elasticity) === 0 &&
          getVelocity(params.sources) === 0 &&
          getVelocity(params.quality) === 0) ||
          (params.mode === "default" &&
            getVelocity(params.origin[0]) === 0 &&
            getVelocity(params.origin[1]) === 0 &&
            getVelocity(params.length) === 0 &&
            getVelocity(params.a) === 0 &&
            getVelocity(params.b) === 0 &&
            getVelocity(params.n) === 0 &&
            getVelocity(params.m) === 0 &&
            getVelocity(params.quality) === 0))
      ) {
        return;
      }

      if (typeof onDraw === "function") {
        onDraw();
      }

      const origin =
        params.mode === "default" && params.origin
          ? ([getValue(params.origin[0]), getValue(params.origin[1])] as [number, number])
          : undefined;
      const anchor = params.mode === "default" && params.anchor ? params.anchor : undefined;
      const length = params.mode === "default" && params.length ? getValue(params.length) : undefined;
      const viewport = origin && anchor && length ? { origin, anchor, length } : {};

      const contours = chladni({
        width: canvas.width,
        height: canvas.height,
        ...(params.mode === "sources"
          ? {
              mode: params.mode,
              symmetry: params.symmetry,
              frequency: getValue(params.frequency),
              phaseShift: getValue(params.phaseShift),
              elasticity: getValue(params.elasticity),
              sources: getValue(params.sources),
              quality: getValue(params.quality),
            }
          : {
              mode: params.mode,
              symmetry: params.symmetry,
              a: getValue(params.a),
              b: getValue(params.b),
              n: getValue(params.n),
              m: getValue(params.m),
              quality: getValue(params.quality),
              ...viewport,
            }),
      });

      drawContours(contours);
    }

    function render() {
      draw(renderMode === "always");
    }

    frame.render(render, true);

    const resizeObserver = new window.ResizeObserver(() => {
      if (!canvas) return;
      const width = canvas.offsetWidth * dpr;
      const height = canvas.offsetHeight * dpr;
      if (width === canvas.width && height === canvas.height) return;
      canvas.width = width;
      canvas.height = height;
      draw(true);
    });
    resizeObserver.observe(canvas);

    return () => {
      cancelFrame(render);
      resizeObserver.disconnect();
    };
  }, [
    onDraw,
    strokeWidth,
    strokeOpacity,
    renderMode,
    stroke,
    ...(params.mode === "sources"
      ? [params.frequency, params.phaseShift, params.elasticity, params.sources, params.quality, params.symmetry]
      : [
          params.a,
          params.b,
          params.n,
          params.m,
          params.quality,
          params.length,
          params.anchor,
          params.origin[0],
          params.origin[1],
          params.symmetry,
        ]),
  ]);

  return <canvas ref={canvasRef} className='tw-absolute tw-inset-0 tw-size-full' />;
}
